/**
 * Admin "Brix & Kvalitet" hub — a single landing page that gathers the five
 * Brix-related tools so the admin dropdown menu stays short. Kept minimal
 * so it renders as an at-a-glance dashboard; each card links to the tool's
 * dedicated page. No functionality lives here — this is a navigation surface.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const TOOLS = [
  {
    to: '/admin/brix-outliers',
    icon: '📊',
    title: 'Brix-outliers',
    desc: 'Opskrifter hvor beregnet vs. mål-Brix ikke matcher, eller hvor mål-Brix falder udenfor typens interval.',
    testid: 'brix-quality-card-outliers',
  },
  {
    to: '/admin/ingredients-missing-brix',
    icon: '🥤',
    title: 'Ingredienser uden Brix',
    desc: 'Ingredienser hvis Brix ikke kan resolves — se antal opskrifter der påvirkes og brug Smart Wizard til at rette dem.',
    testid: 'brix-quality-card-missing',
  },
  {
    to: '/admin/resync-ingredient-brix',
    icon: '🔄',
    title: 'Resync Brix → opskrifter',
    desc: 'Kopiér opdaterede katalog-Brix ind i alle opskrifter der bruger dem. Dry-run inkluderet.',
    testid: 'brix-quality-card-resync',
  },
  {
    to: '/admin/normalize-target-brix',
    icon: '⚙️',
    title: 'Normalisér target_brix',
    desc: 'Engangs-oprydning: sætter target_brix på alle opskrifter til type-defaultet. Renamer også type-aliaser (milk → maelk osv.).',
    testid: 'brix-quality-card-normalize',
  },
  {
    to: '/admin/classify-recipes',
    icon: '🏷',
    title: 'Klassificér opskrifter uden type',
    desc: 'Opskrifter importeret uden Type. Vælg type i bulk og lad systemet auto-udfylde standard-Brix.',
    testid: 'brix-quality-card-classify',
  },
];

const AdminBrixQualityPage = () => (
  <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="admin-brix-quality-page">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Brix & Kvalitet</h1>
      <p className="text-gray-600 text-sm">
        Værktøjer til at analysere og rette Brix-værdier på tværs af katalog og opskrifter.
        Alle værktøjer er skrivebeskyttede indtil du eksplicit bekræfter en ændring.
      </p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {TOOLS.map((t) => (
        <Link
          key={t.to}
          to={t.to}
          className="group block rounded-xl border border-gray-200 bg-white p-5 hover:border-blue-400 hover:shadow-md transition-all"
          data-testid={t.testid}
        >
          <div className="flex items-start gap-3">
            <span className="text-3xl leading-none" aria-hidden="true">{t.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 group-hover:text-blue-700">{t.title}</div>
              <p className="text-sm text-gray-600 mt-1">{t.desc}</p>
            </div>
            <svg
              className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

export default AdminBrixQualityPage;
